import { ChangeDetectorRef, Component, inject, PLATFORM_ID, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AddUserRequest } from '../../models/user.model';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../../../core/services/auth.service';
import { SnackBarService } from '../../../services/snackbar.service';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatError, MatFormField, MatInputModule, MatLabel } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { SuccessError } from '../../../general/components/success-error/success-error';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RecaptchaService } from '../../../../core/services/recaptcha.service';
import { mergeMap } from 'rxjs';
import { RegisterUserRequest } from '../../models/register-user-request.model';
import { GtmService } from '../../../../core/services/gtm.service';
import { HapticsService } from '../../../general/services/haptics.service';
import { LoaderComponent } from "../../../general/components/loader/loader.component";
import { SeoService } from '../../../services/seo.service';
import { environment } from '../../../../../environments/environment';
import { Router } from '@angular/router';

@Component({
  selector: 'app-create-user',
  imports: [
    CommonModule,
    MatButtonModule,
    MatSnackBarModule,
    MatCardModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormField,
    MatInputModule,
    MatSlideToggleModule,
    MatLabel,
    MatError,
    SuccessError,
    LoaderComponent
],
  templateUrl: './create-user.html',
  styleUrl: './create-user.scss',
})
export class CreateUserComponent implements OnInit {
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private recaptchaService = inject(RecaptchaService);
  private snackBar = inject(SnackBarService);
  private haptics = inject(HapticsService);
  private cdr = inject(ChangeDetectorRef);
  private gtm = inject(GtmService);
  private router = inject(Router);
  private snackbar = inject(SnackBarService);
  @Input() isDialogMode = false;
  @Output() registerComplete = new EventEmitter<string>();

  addUserForm: FormGroup;
  submitted = false;
  successful = false;
  loading = false;
  platformId = inject(PLATFORM_ID);
  private seo = inject(SeoService);
  affiliateId: string | null = null;
  constructor() {
    this.addUserForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      passwordConfirm: ['', Validators.required],
      termsAccepted: [false, Validators.requiredTrue] // New control for toggle
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    if(isPlatformBrowser(this.platformId)) {
      document.body.scrollTop = 0;
      this.gtm.pageView({
        page_path: '/create-user',
        page_title: 'Create User | Horizinns'
      });
      this.affiliateId = this.getReferralAgentId();
      console.log('affiliateId', this.affiliateId);
    }
    this.seo.updateMetaTags('Create User', 'User Registration for Horizinns', undefined, `${environment.appUrl}/create-user`);
  }

  getReferralAgentId(): string | null {
    const match = document.cookie.match(new RegExp('(^| )referral_agent_id=([^;]+)'));
    console.log('match', match);
    return match ? match[2] : null;
  }

  // Validator to check if passwords match
  passwordMatchValidator(form: FormGroup) {
    return form.get('password')?.value === form.get('passwordConfirm')?.value
      ? null
      : { mismatch: true };
  }

  agreementChange() {
    this.haptics.click();
  }

  submit = () => {
    this.haptics.click();
    this.addUserForm.markAllAsTouched();
    
    console.log('submit');
    if (this.addUserForm.valid) {
      this.submitted = true;
      this.loading = true;
      const userData: AddUserRequest = {
        firstName: this.addUserForm.get('firstName')?.value,
        lastName: this.addUserForm.get('lastName')?.value,
        email: this.addUserForm.get('email')?.value,
        password: this.addUserForm.get('password')?.value,
        username: this.addUserForm.get('email')?.value,
        affiliateId: this.affiliateId ? parseInt(this.affiliateId) : undefined,
        role: 'user'
      };

      this.recaptchaService.execute('register_user').pipe(
        mergeMap((token: string) => {
          const request: RegisterUserRequest = {
            user: userData,
            recaptchaToken: token
          };

          return this.authService.register(request)
        })
      ).subscribe({
        next: () => {
          this.loading = false;
          this.successful = true;
          this.deleteReferralCookie();
          this.trackCreatedUser(userData);
          this.snackbar.open('User created successfully, please check your email for a verification code', '', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
          });
          
          if (this.isDialogMode) {
            this.registerComplete.emit(userData.email);
          } else {
            this.router.navigate(['/login/verify']);
          }
          
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error:', error);
          this.loading = false;
          this.successful = false;
          this.cdr.detectChanges();
          this.snackBar.error(error.error?.message || error.error || 'Registration failed');
        }
      });
    } else {
      this.submitted = false;
      console.log('addUserForm not valid');
      this.addUserForm.updateValueAndValidity();
    }
  }

  trackCreatedUser(user: AddUserRequest) {
    this.gtm.pushEvent('created_user', {
      first_name: user.firstName,
      last_name: user.lastName,
      user_email: user.email
    });
  }

  deleteReferralCookie(): void {
    document.cookie = `referral_agent_id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
  }
}
